export class Preview {
    private container: HTMLElement;
    private output: HTMLElement;
    private scale: number = 1;
    private panX: number = 0;
    private panY: number = 0;
    private isPanning: boolean = false;
    private startX: number = 0;
    private startY: number = 0;
    private svgWidth: number = 0;
    private svgHeight: number = 0;

    private readonly MIN_SCALE = 0.1;
    private readonly MAX_SCALE = 5;
    private readonly SCALE_STEP = 0.1;

    constructor() {
        this.container = document.getElementById('svg-container') as HTMLElement;
        this.output = document.getElementById('svg-output') as HTMLElement;

        if (!this.container || !this.output) {
            throw new Error('Required preview elements not found');
        }

        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        // Pan controls
        this.container.addEventListener('mousedown', this.startPan.bind(this));
        this.container.addEventListener('mousemove', this.doPan.bind(this));
        this.container.addEventListener('mouseup', this.endPan.bind(this));
        this.container.addEventListener('mouseleave', this.endPan.bind(this));

        // Wheel zoom
        this.container.addEventListener('wheel', (e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -this.SCALE_STEP : this.SCALE_STEP;
            this.zoomBy(delta);
        });
    }

    displaySVG(svgString: string): void {
        this.output.innerHTML = svgString;
        this.resetZoom();

        // Get SVG dimensions
        const svgElement = this.output.querySelector('svg');
        if (svgElement) {
            const viewBox = svgElement.getAttribute('viewBox');
            if (viewBox) {
                const parts = viewBox.split(' ');
                this.svgWidth = parseFloat(parts[2]);
                this.svgHeight = parseFloat(parts[3]);
            } else {
                this.svgWidth = svgElement.width.baseVal.value || 800;
                this.svgHeight = svgElement.height.baseVal.value || 600;
            }
        }

        this.updatePanCursor();
    }

    clear(): void {
        this.output.innerHTML =
            '<p style="color: #7f8c8d; text-align: center;">Enter Mermaid syntax to see the rendered diagram</p>';
        this.resetZoom();
        this.svgWidth = 0;
        this.svgHeight = 0;
        this.updatePanCursor();
    }

    zoomBy(delta: number): void {
        const newScale = Math.max(this.MIN_SCALE, Math.min(this.MAX_SCALE, this.scale + delta));
        this.scale = newScale;
        this.updateTransform();
        this.updateZoomLevel();
    }

    resetZoom(): void {
        this.scale = 1;
        this.panX = 0;
        this.panY = 0;
        this.updateTransform();
        this.updateZoomLevel();
    }

    private updateTransform(): void {
        this.output.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.scale})`;
    }

    private updateZoomLevel(): void {
        const zoomLevel = document.getElementById('zoom-level');
        if (zoomLevel) {
            zoomLevel.textContent = Math.round(this.scale * 100) + '%';
        }
    }

    private canPan(): boolean {
        const containerRect = this.container.getBoundingClientRect();
        const scaledWidth = this.svgWidth * this.scale;
        const scaledHeight = this.svgHeight * this.scale;
        return scaledWidth > containerRect.width || scaledHeight > containerRect.height;
    }

    private startPan(e: MouseEvent): void {
        if (!this.canPan()) return;

        this.isPanning = true;
        this.startX = e.clientX - this.panX;
        this.startY = e.clientY - this.panY;
        this.container.classList.add('panning');
    }

    private doPan(e: MouseEvent): void {
        if (!this.isPanning) return;

        e.preventDefault();
        this.panX = e.clientX - this.startX;
        this.panY = e.clientY - this.startY;
        this.updateTransform();
    }

    private endPan(): void {
        if (!this.isPanning) return;

        this.isPanning = false;
        this.container.classList.remove('panning');
    }

    private updatePanCursor(): void {
        if (this.canPan()) {
            this.container.classList.add('can-pan');
        } else {
            this.container.classList.remove('can-pan');
        }
    }
}
